/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback } from "react";
import classnames from "classnames";
import * as R from "ramda";
import { Link } from "react-router-dom";
import Select from "react-select";

import ExternalLink from "components/ExternalLink";
import Icon from "components/Icon";
import Loader from "components/Loader";
import GithubLink from "components/config/GithubLink";
import { useAnswerContext } from "contexts/answerContext";
import { useIsInConfigSection } from "contexts/applicationSection";
import { useCurrentTenant, useLockVersion } from "contexts/currentTenant";
import { useServerVariables } from "contexts/serverVariables";
import { useSession } from "contexts/session";
import useUIContext from "contexts/ui";
import { useVersions } from "queries/config/versions";
import { getTenantVersionIsHead } from "reducers/tenant";

import styles from "./AdminBar.scss";

const classNameForDeliveryStage = (deliveryStage) =>
  R.propOr(styles.stageStaging, deliveryStage, {
    production: styles.stageProduction,
    local: styles.stageLocal,
  });

const useAdminBarState = () => {
  const { isSuperuser: visible } = useSession();
  const { adminBarOpened: expanded, toggleAdminBarOpened: toggle } = useUIContext();
  const tenant = useCurrentTenant();
  const { version_number: activeVersion } = tenant;

  const activeIsHead = getTenantVersionIsHead(tenant);
  const isInConfig = useIsInConfigSection();

  return {
    visible,
    expanded: isInConfig || expanded,
    toggle: isInConfig ? null : toggle,
    activeVersion,
    activeIsHead,
  };
};

export const AdminBarHandle = ({ className = [] }) => {
  const { visible, expanded, toggle, activeVersion } = useAdminBarState();
  const { deliveryStage } = useServerVariables();

  if (!visible || expanded) return null;
  return (
    <div
      title="Admin Bar"
      data-version={activeVersion}
      data-admin-bar
      className={classnames(styles.collapsed, classNameForDeliveryStage(deliveryStage), className)}
      onClick={toggle}
    >
      <Icon
        icon="chevron-double-down"
        faStyle="solid"
        size="xs"
        className={[styles.collapsedIcon]}
      />
    </div>
  );
};

const toOptions = R.map(({ name, number, current }) => ({
  value: number,
  label: `${number}. ${current ? "CURRENT: " : ""}${name}`,
}));

export const AdminBar = () => {
  const changeVersion = useLockVersion();
  const { deliveryStage } = useServerVariables();
  const { record: project = {} } = useAnswerContext() || {};
  const { visible, expanded, toggle, activeVersion, activeIsHead } = useAdminBarState();
  const { data: versions = [], isLoading: isLoadingVersions } = useVersions({
    enabled: visible && expanded,
    select: toOptions,
  });

  const selectedValue = versions.find((o) => o.value === activeVersion);

  const onChangeVersion = useCallback(
    ({ value: version }) => {
      changeVersion(version);
    },
    [changeVersion],
  );

  const isLoading = isLoadingVersions;
  if (!visible || !expanded) return null;

  return (
    <div className={styles.wrapper} data-admin-bar data-version={activeVersion}>
      <div
        className={classnames(styles.stage, classNameForDeliveryStage(deliveryStage))}
        onClick={toggle}
      >
        <div>
          <Icon icon="compass" faStyle="solid" />
          <span>{deliveryStage}</span>
        </div>
      </div>
      <div className={styles.git}>
        <GithubLink />
      </div>
      {project.id && (
        <div>
          <ExternalLink href={`/admin/projects/${project.id}/debug`} className="btn-sm" hideIcon>
            Debug
          </ExternalLink>
        </div>
      )}
      <div className={styles.version}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Select
              name="version"
              value={selectedValue}
              onChange={onChangeVersion}
              onBlur={R.T}
              options={versions}
              classNamePrefix="react-select"
            />
            <Link to={`/config/versions/${activeVersion}`} className="btn-sm">
              {activeIsHead ? "Commit" : "View"}
            </Link>
          </>
        )}
      </div>
      {toggle && (
        <div title="close" data-close className={styles.close} onClick={toggle}>
          <Icon icon="times" />
        </div>
      )}
    </div>
  );
};
